import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../services/api';
import { todayKey } from '../../utils/date';

function SearchOrderModal({ isOpen, onClose, selectedDate, onReschedule }) {
  const today = todayKey();
  const [searchFromDate, setSearchFromDate] = useState(today);
  const [searchToDate, setSearchToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [removeDeliveryItems, setRemoveDeliveryItems] = useState(true);
  const [excludeAllocated, setExcludeAllocated] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // Initialize reschedule date with selectedDate
  useEffect(() => {
    if (selectedDate) {
      setRescheduleDate(selectedDate);
    }
  }, [selectedDate]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchFromDate(today);
      setSearchToDate('');
      setSearchTerm('');
      setSearchResults([]);
      setSelectedOrders([]);
      setExcludeAllocated(true);
      setExpandedOrders(new Set());
      if (selectedDate) {
        setRescheduleDate(selectedDate);
      }
    }
  }, [isOpen, selectedDate]);

  const toggleOrderDetails = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const params = {};
      
      // Add date range if specified
      // If only From date is set, treat it as a single date search (From = To)
      if (searchFromDate) {
        if (searchToDate) {
          params.schedule_date_from = searchFromDate;
          params.schedule_date_to = searchToDate;
        } else {
          // Single date search - use From as both From and To
          params.schedule_date_from = searchFromDate;
          params.schedule_date_to = searchFromDate;
        }
      }
      
      // Add search term if specified (searches customer name, phone, email, address)
      if (searchTerm) {
        params.search = searchTerm;
      }

      const results = await orderAPI.searchOrders(params);
      let filteredResults = results.data || results;
      
      // Exclude orders that are allocated to a delivery run
      if (excludeAllocated) {
        filteredResults = filteredResults.filter((order) => {
          const isAllocated = order.deliveries && order.deliveries.some(
            (delivery) => delivery.delivery_run_id
          );
          return !isAllocated;
        });
      }
      
      setSearchResults(filteredResults);
    } catch (err) {
      console.error('Error searching orders:', err);
      alert('Failed to search orders');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === searchResults.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(searchResults.map((order) => order.id));
    }
  };

  const handleReschedule = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order');
      return;
    }

    if (!rescheduleDate) {
      alert('Please select a reschedule date');
      return;
    }

    setIsRescheduling(true);
    try {
      await orderAPI.batchReschedule(selectedOrders, rescheduleDate, { removeDeliveryItems });
      alert('Orders rescheduled successfully');
      setSelectedOrders([]);
      setSearchResults([]);
      if (onReschedule) {
        onReschedule();
      }
      onClose();
    } catch (err) {
      console.error('Error rescheduling orders:', err);
      alert(err.response?.data?.message || 'Failed to reschedule orders');
    } finally {
      setIsRescheduling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal search-order-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
        <div className="modal-header">
          <h2>Search Orders</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Search Form */}
          <div className="search-form">
            <div className="search-row">
              <div className="form-group">
                <label htmlFor="search-from-date">Schedule Date From</label>
                <input
                  type="date"
                  id="search-from-date"
                  value={searchFromDate}
                  onChange={(e) => setSearchFromDate(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="search-to-date">Schedule Date To</label>
                <input
                  type="date"
                  id="search-to-date"
                  value={searchToDate}
                  onChange={(e) => setSearchToDate(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="search-term">Customer Name, Phone, Email, or Address</label>
              <input
                type="text"
                id="search-term"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
                placeholder="Search by customer details..."
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={excludeAllocated}
                  onChange={(e) => setExcludeAllocated(e.target.checked)}
                />
                Exclude orders allocated to a delivery run
              </label>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <div className="results-header">
                <label className="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === searchResults.length && searchResults.length > 0}
                    onChange={handleSelectAll}
                  />
                  Select All ({searchResults.length})
                </label>
              </div>

              <div className="results-list">
                {searchResults.map((order) => {
                  const isExpanded = expandedOrders.has(order.id);
                  return (
                    <div key={order.id} className="result-item">
                      <label className="result-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => handleSelectOrder(order.id)}
                        />
                      </label>
                      <div className="result-details">
                        <div className="result-header">
                          <div className="result-customer">
                            {order.customer?.fullname || 'Unknown Customer'}
                          </div>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleOrderDetails(order.id)}
                            style={{ fontSize: '11px', padding: '2px 8px', float: 'right' }}
                          >
                            {isExpanded ? 'Hide Details' : 'Details'}
                          </button>
                        </div>
                        <div className="result-info">
                          {order.delivery_address && (
                            <span className="result-address">{order.delivery_address}</span>
                          )}
                          {order.customer?.phone && (
                            <span className="result-phone">{order.customer.phone}</span>
                          )}
                          {order.customer?.email && (
                            <span className="result-email">{order.customer.email}</span>
                          )}
                        </div>
                        <div className="result-meta">
                          <span className="result-date">
                            Scheduled: {formatDate(order.schedule_date)}
                          </span>
                          <span className="result-order-date">
                            Order: {formatDate(order.order_date)}
                          </span>
                        </div>
                        {isExpanded && (
                          <div className="result-expanded">
                            {order.note && (
                              <div className="expanded-notes">
                                <strong>Notes:</strong> {order.note}
                              </div>
                            )}
                            {order.order_lines && order.order_lines.length > 0 && (
                              <div className="expanded-products" style={{ marginTop: '10px' }}>
                                <strong>Products:</strong>
                                <table className="products-table" style={{ width: '50%', marginTop: '5px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Product</th>
                                      <th style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Size</th>
                                      <th style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Qty</th>
                                      <th style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Cubes</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.order_lines.map((line, idx) => (
                                      <tr key={idx}>
                                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{line.product?.description || line.product_id}</td>
                                        <td style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #eee' }}>{line.size || 1}</td>
                                        <td style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #eee' }}>{line.qty || 1}</td>
                                        <td style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{(line.size || 1) * (line.qty || 1)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {searchResults.length === 0 && !isSearching && (
            <div className="no-results">
              <p>No orders found. Try adjusting your search criteria.</p>
            </div>
          )}

          {isSearching && (
            <div className="loading-results">
              <div className="spinner-small"></div>
              <span>Searching orders...</span>
            </div>
          )}
        </div>

        {/* Footer with Reschedule Date */}
        {searchResults.length > 0 && (
          <div className="modal-footer">
            <div className="reschedule-controls">
              <div className="form-group">
                <label htmlFor="reschedule-date">Re-Schedule Date</label>
                <input
                  type="date"
                  id="reschedule-date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">Remove delivery</label>
                <input
                  type="checkbox"
                  checked={removeDeliveryItems}
                  onChange={(e) => setRemoveDeliveryItems(e.target.checked)}
                />
              </div>
              
              <button
                className="btn btn-primary"
                onClick={handleReschedule}
                disabled={selectedOrders.length === 0 || isRescheduling}
              >
                {isRescheduling ? 'Rescheduling...' : `Re-Schedule (${selectedOrders.length})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchOrderModal;